'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type CategoryBadgesScrollerProps = {
  children: ReactNode;
  previousLabel: string;
  nextLabel: string;
};

export function CategoryBadgesScroller({
  children,
  previousLabel,
  nextLabel,
}: CategoryBadgesScrollerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollButtons();

    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(el);
    el.addEventListener('scroll', updateScrollButtons, { passive: true });

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', updateScrollButtons);
    };
  }, [updateScrollButtons, children]);

  const scrollByDirection = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(el.clientWidth * 0.75, 160);
    el.scrollBy({
      left: direction === 'left' ? -step : step,
      behavior: 'smooth',
    });
  };

  // Keep -translate-y-1/2 on :active — Button's default active:translate-y-px
  // replaces the whole translate and makes the control jump down on press.
  const navButtonClass = cn(
    'absolute top-1/2 z-10 hidden size-8 -translate-y-1/2 active:not-aria-[haspopup]:-translate-y-1/2 md:inline-flex',
    'rounded-full border-0 shadow-none',
    'bg-secondary text-secondary-foreground hover:bg-secondary/80'
  );

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="flex w-full gap-2 overflow-x-auto scroll-smooth scrollbar-hide"
      >
        {children}
      </div>

      {canScrollLeft && (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-[1] hidden w-12 bg-gradient-to-r from-background to-transparent md:block"
            aria-hidden
          />
          <Button
            type="button"
            variant="secondary"
            size="icon-sm"
            className={cn(navButtonClass, 'left-0')}
            onClick={() => scrollByDirection('left')}
            aria-label={previousLabel}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </>
      )}

      {canScrollRight && (
        <>
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-[1] hidden w-12 bg-gradient-to-l from-background to-transparent md:block"
            aria-hidden
          />
          <Button
            type="button"
            variant="secondary"
            size="icon-sm"
            className={cn(navButtonClass, 'right-0')}
            onClick={() => scrollByDirection('right')}
            aria-label={nextLabel}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}
